import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { SelectWithAdd } from '../components/ui/select-with-add';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye,
  List,
  Settings,
  BookOpen
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { api } from '../lib/api';

interface CodelistType {
  id: number;
  type: string;
  name: string;
  description: string;
  settings: {
    allow_add: boolean;
    required: boolean;
    multiple: boolean;
    searchable: boolean;
    parent_codelist_type?: string;
  };
  parent_codelist_type?: string;
  sort_order: number;
  is_active: boolean;
}

interface CodelistItem {
  id: number;
  codelist_type: string;
  code: string;
  name: string;
  description: string;
  parent_code?: string;
  sort_order: number;
  is_active: boolean;
  is_system: boolean;
  created_at: string;
}

const CodelistsPage: React.FC = () => {
  const [codelistTypes, setCodelistTypes] = useState<CodelistType[]>([]);
  const [selectedType, setSelectedType] = useState<string>('');
  const [items, setItems] = useState<CodelistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dependentTypes, setDependentTypes] = useState<CodelistType[]>([]);
  const [selectedParentItem, setSelectedParentItem] = useState<string>('');
  const [hierarchyView, setHierarchyView] = useState(false);
  const { toast } = useToast();
  const [showAddType, setShowAddType] = useState(false);
  const [editingType, setEditingType] = useState<CodelistType | null>(null);
  const [typeForm, setTypeForm] = useState({
    type: '',
    name: '',
    description: '',
    sort_order: 0,
    is_active: true,
    allow_add: true,
    required: false,
    multiple: false,
    searchable: true,
    parent_codelist_type: ''
  });

  const [showAddItem, setShowAddItem] = useState(false);
  const [editingItem, setEditingItem] = useState<CodelistItem | null>(null);
  const [itemForm, setItemForm] = useState({
    code: '',
    name: '',
    description: '',
    parent_code: '',
    sort_order: 0,
    is_active: true
  });

  // Load codelist types on component mount
  useEffect(() => {
    loadCodelistTypes();
  }, []);

  // Load items when type is selected
  useEffect(() => {
    if (selectedType) {
      loadItems();
      loadDependentTypes();
    }
  }, [selectedType]);

  // Reset parent selection when type changes
  useEffect(() => {
    setSelectedParentItem('');
    setHierarchyView(false);
  }, [selectedType]);

  const loadCodelistTypes = async () => {
    try {
      setLoading(true);
      const response = await api.get<{ success: boolean; data: CodelistType[] }>('/codelists/types');
      if (response.success) {
        setCodelistTypes(response.data);
        if (response.data.length > 0 && !selectedType) {
          setSelectedType(response.data[0].type);
        }
      }
    } catch (error) {
      console.error('Failed to load codelist types:', error);
      toast({
        title: "Error",
        description: "Failed to load codelist types",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadItems = async () => {
    if (!selectedType) return;
    
    try {
      const response = await api.get<{ success: boolean; data: CodelistItem[] }>(`/codelists/${selectedType}`);
      if (response.success) {
        setItems(response.data);
      }
    } catch (error) {
      console.error('Failed to load codelist items:', error);
      toast({
        title: "Error",
        description: "Failed to load codelist items",
        variant: "destructive",
      });
    }
  };

  const loadDependentTypes = async () => {
    if (!selectedType) return;
    
    try {
      const response = await api.get<{ success: boolean; data: CodelistType[] }>(`/codelists/dependent/${selectedType}`);
      if (response.success) {
        setDependentTypes(response.data);
      }
    } catch (error) {
      console.error('Failed to load dependent types:', error);
      // Don't show error toast for this as it's optional functionality
      setDependentTypes([]);
    }
  };


  const submitTypeForm = async () => {
    try {
      const payload: any = {
        type: typeForm.type.trim(),
        name: typeForm.name.trim(),
        description: typeForm.description.trim(),
        sort_order: Number(typeForm.sort_order) || 0,
        is_active: typeForm.is_active,
        settings: {
          allow_add: typeForm.allow_add,
          required: typeForm.required,
          multiple: typeForm.multiple,
          searchable: typeForm.searchable
        }
      };

      // Add parent codelist type if specified
      if (typeForm.parent_codelist_type) {
        payload.parent_codelist_type = typeForm.parent_codelist_type;
      }

      if (editingType) {
        await api.put(`/codelists/types/${editingType.type}`, payload);
        toast({ title: 'Saved', description: 'Codelist type updated' });
      } else {
        await api.post('/codelists/types', payload);
        toast({ title: 'Created', description: 'Codelist type created' });
      }

      setShowAddType(false);
      setEditingType(null);
      setTypeForm({ type: '', name: '', description: '', sort_order: 0, is_active: true, allow_add: true, required: false, multiple: false, searchable: true, parent_codelist_type: '' });
      await loadCodelistTypes();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Failed to save codelist type', variant: 'destructive' });
    }
  };

  const startEditType = (type: CodelistType) => {
    setEditingType(type);
    setTypeForm({
      type: type.type,
      name: type.name,
      description: type.description || '',
      sort_order: type.sort_order,
      is_active: type.is_active,
      allow_add: !!type.settings?.allow_add,
      required: !!type.settings?.required,
      multiple: !!type.settings?.multiple,
      searchable: !!type.settings?.searchable,
      parent_codelist_type: type.parent_codelist_type || ''
    });
    setShowAddType(true);
  };

  const submitItemForm = async () => {
    if (!selectedType) return;
    try {
      const payload: any = {
        codelist_type: selectedType,
        code: itemForm.code.trim(),
        name: itemForm.name.trim(),
        description: itemForm.description.trim(),
        sort_order: Number(itemForm.sort_order) || 0,
        is_active: itemForm.is_active
      };

      // Add parent code if specified
      if (itemForm.parent_code) {
        payload.parent_code = itemForm.parent_code;
      }

      if (editingItem) {
        await api.put(`/codelists/items/${editingItem.id}`, payload);
        toast({ title: 'Saved', description: 'Item updated' });
      } else {
        await api.post('/codelists/items', payload);
        toast({ title: 'Created', description: 'Item created' });
      }

      setShowAddItem(false);
      setEditingItem(null);
      setItemForm({ code: '', name: '', description: '', parent_code: '', sort_order: 0, is_active: true });
      await loadItems();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Failed to save item', variant: 'destructive' });
    }
  };

  const handleDeleteItem = async (itemId: number) => {
    if (!selectedType) return;
    
    // Find the item to get its code
    const item = items.find(i => i.id === itemId);
    if (!item) return;

    try {
      // Check usage before deletion
      const usageResponse = await api.get<{ success: boolean; data: any }>(`/codelists/${selectedType}/${item.code}/usage`);
      
      if (usageResponse.success && usageResponse.data.isUsed) {
        const usage = usageResponse.data;
        const usageDetails = usage.usedIn.map((u: any) => `${u.displayName} (${u.count})`).join(', ');
        
        toast({
          title: "Cannot Delete",
          description: `This item is used in: ${usageDetails}. Remove references first.`,
          variant: "destructive",
        });
        return;
      }

      // Confirm deletion
      if (!window.confirm(`Are you sure you want to delete "${item.name}"?`)) {
        return;
      }

      const response = await api.delete<{ success: boolean; message: string }>(`/codelists/items/${itemId}`);
      if (response.success) {
        toast({
          title: "Success",
          description: "Item deleted successfully",
        });
        loadItems(); // Reload the list
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete item",
        variant: "destructive",
      });
    }
  };

  const handleDeleteCodelistType = async (type: CodelistType) => {
    try {
      // Check usage before deletion
      const usageResponse = await api.get<{ success: boolean; data: any }>(`/codelists/${type.type}/usage`);
      
      if (usageResponse.success && usageResponse.data.isUsed) {
        const usage = usageResponse.data;
        const usageDetails = usage.usedIn.map((u: any) => `${u.displayName} (${u.count})`).join(', ');
        
        toast({
          title: "Cannot Delete",
          description: `This codelist is used in: ${usageDetails}. Remove all references first.`,
          variant: "destructive",
        });
        return;
      }

      // Confirm deletion
      if (!window.confirm(`Are you sure you want to delete the entire codelist "${type.name}"? This will also delete all its items.`)) {
        return;
      }

      const response = await api.delete<{ success: boolean; message: string }>(`/codelists/types/${type.type}`);
      if (response.success) {
        toast({
          title: "Success",
          description: "Codelist deleted successfully",
        });
        
        // Reset selection if deleted type was selected
        if (selectedType === type.type) {
          setSelectedType('');
          setItems([]);
        }
        
        loadCodelistTypes(); // Reload the list
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete codelist",
        variant: "destructive",
      });
    }
  };

  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedTypeConfig = codelistTypes.find(t => t.type === selectedType);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Code Lists</h1>
          <p className="text-muted-foreground">
            Manage system code lists and predefined values for forms
          </p>
        </div>
        <Button onClick={() => { setEditingType(null); setTypeForm({ type: '', name: '', description: '', sort_order: 0, is_active: true, allow_add: true, required: false, multiple: false, searchable: true, parent_codelist_type: '' }); setShowAddType(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          Add Codelist Type
        </Button>
      </div>

      {/* Add/Edit Type Form */}
      {showAddType && (
        <Card>
          <CardHeader>
            <CardTitle>{editingType ? 'Edit Codelist Type' : 'Add Codelist Type'}</CardTitle>
            <CardDescription>Define metadata and behavior for the codelist</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2">
              <Input placeholder="Type (e.g., document_categories)" value={typeForm.type} onChange={(e) => setTypeForm(prev => ({ ...prev, type: e.target.value }))} disabled={!!editingType} />
              <Input placeholder="Name" value={typeForm.name} onChange={(e) => setTypeForm(prev => ({ ...prev, name: e.target.value }))} />
              <Input placeholder="Description" value={typeForm.description} onChange={(e) => setTypeForm(prev => ({ ...prev, description: e.target.value }))} />
              <Input placeholder="Sort order" type="number" value={typeForm.sort_order} onChange={(e) => setTypeForm(prev => ({ ...prev, sort_order: Number(e.target.value) }))} />
              <select 
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={typeForm.parent_codelist_type} 
                onChange={(e) => setTypeForm(prev => ({ ...prev, parent_codelist_type: e.target.value }))}
              >
                <option value="">No parent (independent codelist)</option>
                {codelistTypes.map(type => (
                  <option key={type.type} value={type.type} disabled={type.type === typeForm.type}>
                    {type.name} ({type.type})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-2 mt-3 flex-wrap">
              <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={typeForm.is_active} onChange={(e) => setTypeForm(prev => ({ ...prev, is_active: e.target.checked }))} />Active</label>
              <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={typeForm.allow_add} onChange={(e) => setTypeForm(prev => ({ ...prev, allow_add: e.target.checked }))} />Allow add</label>
              <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={typeForm.multiple} onChange={(e) => setTypeForm(prev => ({ ...prev, multiple: e.target.checked }))} />Multiple</label>
              <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={typeForm.searchable} onChange={(e) => setTypeForm(prev => ({ ...prev, searchable: e.target.checked }))} />Searchable</label>
              <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={typeForm.required} onChange={(e) => setTypeForm(prev => ({ ...prev, required: e.target.checked }))} />Required</label>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={submitTypeForm}>{editingType ? 'Save' : 'Create'}</Button>
              <Button variant="outline" onClick={() => { setShowAddType(false); setEditingType(null); }}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Codelist Type Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <List className="h-5 w-5 mr-2" />
            Codelist Types
          </CardTitle>
          <CardDescription>
            Select a codelist type to view and manage its items
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {codelistTypes.map((type) => (
              <Card 
                key={type.type} 
                className={`cursor-pointer hover:shadow-md transition-shadow ${
                  selectedType === type.type ? 'ring-2 ring-blue-500' : ''
                }`}
                onClick={() => setSelectedType(type.type)}
              >
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium">{type.name}</h3>
                    <Badge variant={type.is_active ? 'default' : 'secondary'}>
                      {type.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{type.description}</p>
                  {type.parent_codelist_type && (
                    <p className="text-xs text-blue-600 mb-2">
                      Depends on: {codelistTypes.find(t => t.type === type.parent_codelist_type)?.name || type.parent_codelist_type}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1">
                    {type.settings.allow_add && <Badge variant="outline" className="text-xs">Add allowed</Badge>}
                    {type.settings.multiple && <Badge variant="outline" className="text-xs">Multiple</Badge>}
                    {type.settings.searchable && <Badge variant="outline" className="text-xs">Searchable</Badge>}
                    {type.settings.required && <Badge variant="outline" className="text-xs">Required</Badge>}
                    {dependentTypes.length > 0 && selectedType === type.type && <Badge variant="outline" className="text-xs">Has dependents</Badge>}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); startEditType(type); }}>
                      <Edit className="h-3 w-3 mr-1" /> Edit
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={(e) => { e.stopPropagation(); handleDeleteCodelistType(type); }}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-3 w-3 mr-1" /> Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Selected Codelist Items */}
      {selectedType && selectedTypeConfig && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="flex items-center">
                  <BookOpen className="h-5 w-5 mr-2" />
                  {selectedTypeConfig.name} Items
                </CardTitle>
                <CardDescription>
                  Manage items in the {selectedTypeConfig.name} codelist
                </CardDescription>
              </div>
              {selectedTypeConfig.settings.allow_add && (
                <Button onClick={() => { setEditingItem(null); setItemForm({ code: '', name: '', description: '', parent_code: '', sort_order: 0, is_active: true }); setShowAddItem(true); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Item
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {showAddItem && (
              <div className="mb-4 p-4 border rounded">
                <div className="grid gap-3 md:grid-cols-2">
                  <Input placeholder="Code" value={itemForm.code} onChange={(e) => setItemForm(prev => ({ ...prev, code: e.target.value }))} />
                  <Input placeholder="Name" value={itemForm.name} onChange={(e) => setItemForm(prev => ({ ...prev, name: e.target.value }))} />
                  <Input placeholder="Description" value={itemForm.description} onChange={(e) => setItemForm(prev => ({ ...prev, description: e.target.value }))} />
                  <Input placeholder="Sort order" type="number" value={itemForm.sort_order} onChange={(e) => setItemForm(prev => ({ ...prev, sort_order: Number(e.target.value) }))} />
                  
                  {/* Parent code selector for dependent codelists */}
                  {selectedTypeConfig?.parent_codelist_type && (
                    <div className="md:col-span-2">
                      <SelectWithAdd
                        label={`Parent ${codelistTypes.find(t => t.type === selectedTypeConfig.parent_codelist_type)?.name || 'Item'}`}
                        value={itemForm.parent_code}
                        onChange={(value) => setItemForm(prev => ({ ...prev, parent_code: value as string }))}
                        codelistType={selectedTypeConfig.parent_codelist_type}
                        placeholder="Select parent item..."
                        required={true}
                        disabled={false}
                      />
                    </div>
                  )}
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={submitItemForm}>{editingItem ? 'Save' : 'Create'}</Button>
                  <Button size="sm" variant="outline" onClick={() => { setShowAddItem(false); setEditingItem(null); }}>Cancel</Button>
                </div>
              </div>
            )}
            {/* Search */}
            {selectedTypeConfig.settings.searchable && (
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
            )}

            {/* Items List */}
            <div className="space-y-2">
              {filteredItems.map((item) => (
                <Card key={item.id} className="hover:shadow-sm transition-shadow">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-medium">{item.name}</h3>
                          <Badge variant="outline" className="text-xs">{item.code}</Badge>
                          {item.parent_code && <Badge variant="secondary" className="text-xs">Parent: {item.parent_code}</Badge>}
                          {item.is_system && <Badge variant="secondary" className="text-xs">System</Badge>}
                          {!item.is_active && <Badge variant="destructive" className="text-xs">Inactive</Badge>}
                        </div>
                        {item.description && (
                          <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                        )}
                      </div>
                      <div className="flex space-x-1">
                        <Button variant="outline" size="sm">
                          <Eye className="h-3 w-3" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => { setEditingItem(item); setItemForm({ code: item.code, name: item.name, description: item.description, parent_code: item.parent_code || '', sort_order: item.sort_order, is_active: item.is_active }); setShowAddItem(true); }}>
                          <Edit className="h-3 w-3" />
                        </Button>
                        {!item.is_system && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDeleteItem(item.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {filteredItems.length === 0 && (
                <div className="text-center py-8">
                  <List className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No items found</h3>
                  <p className="text-muted-foreground">
                    {searchTerm 
                      ? "No items match your search criteria."
                      : "This codelist doesn't have any items yet."
                    }
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CodelistsPage;
